import React, { useState, useEffect } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import ButtonV2 from 'components/ButtonV2';
import Card from 'components/Card';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import { Select } from 'components/FormFields';
import Tabs from 'components/Tabs';
import Toaster from 'components/Toaster';
import { useGetAccount } from '../gql/queries/useGetAccount';
import AddressesTab from './components/AddressesTab';
import DetailsTab from './components/DetailsTab';
import DivisionsTab from './components/DivisionsTab';
import HardwareTab from './components/HardwareTab';
import LogosTab from './components/LogosTab';
import NotesTab from './components/NotesTab';
import PaymentsTab from './components/PaymentsTab';
import POSTab from './components/POSTab';
import StaffTab from './components/StaffTab';
import TicketsTab from './components/TicketsTab';
import WaiversTab from './components/WaiversTab';
import { useUpdateAccountDetails } from './gql/useUpdateAccountDetails';

// Page wrapper - ensures full viewport coverage with theme background
const PageWrapper = styled.div`
  min-height: 100vh;
  background: ${({ theme }) =>
    theme?.colors?.background?.primary ||
    (theme?.name === 'dark' ? '#1a1a2e' : '#f8fafc')};
  font-family: ${({ theme }) =>
    theme?.fonts?.primary ||
    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'};
`;

// Page container - from StyleGuide Layout patterns
const Container = styled.div`
  padding: 30px;
  max-width: 1400px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 20px 16px;
  }
`;

// Page header - from StyleGuide Overview pattern
const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 32px;
  gap: 24px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const HeaderLeft = styled.div``;

const HeaderRight = styled.div`
  display: flex;
  gap: 12px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    justify-content: flex-end;
  }
`;

const Title = styled.h1`
  font-size: 36px;
  font-weight: 700;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 12px 0;
`;

const Subtitle = styled.p`
  font-size: 18px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  margin: 0;
  line-height: 1.6;
`;

// Button wrapper to control width for header buttons
const HeaderButton = styled.div`
  width: auto;

  button {
    width: auto;
    padding: 10px 24px;
  }
`;

// Account status toggle
const StatusToggle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 16px;
  background: ${({ isInactive, theme }) =>
    isInactive
      ? theme.name === 'dark'
        ? 'rgba(239, 68, 68, 0.12)'
        : 'rgba(239, 68, 68, 0.08)'
      : theme.name === 'dark'
      ? 'rgba(34, 197, 94, 0.12)'
      : 'rgba(34, 197, 94, 0.08)'};
  border: 1px solid
    ${({ isInactive }) =>
      isInactive ? 'rgba(239, 68, 68, 0.3)' : 'rgba(34, 197, 94, 0.3)'};
  border-radius: 8px;
`;

const StatusLabel = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${({ isInactive }) => (isInactive ? '#ef4444' : '#22c55e')};
`;

const StatusToggleSwitch = styled.label`
  position: relative;
  display: inline-block;
  width: 40px;
  height: 22px;
  cursor: pointer;
`;

const StatusToggleInput = styled.input`
  opacity: 0;
  width: 0;
  height: 0;

  &:checked + span {
    background: #22c55e;
  }

  &:checked + span:before {
    transform: translateX(18px);
  }
`;

const StatusToggleSlider = styled.span`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ef4444;
  transition: all 0.3s ease;
  border-radius: 22px;

  &:before {
    position: absolute;
    content: '';
    height: 16px;
    width: 16px;
    left: 3px;
    top: 3px;
    background-color: white;
    transition: all 0.3s ease;
    border-radius: 50%;
  }
`;

// Desktop tabs wrapper - adds margin and hides on mobile
const DesktopTabsWrapper = styled.div`
  margin-bottom: 24px;

  @media (max-width: 768px) {
    display: none;
  }
`;

// Mobile select container - shown only on mobile
const MobileTabSelect = styled.div`
  display: none;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    display: block;
  }
`;

// Placeholder for empty tabs
const PlaceholderContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
  color: ${({ theme }) => (theme.name === 'dark' ? '#64748b' : '#94a3b8')};
  font-size: 16px;
  gap: 16px;
`;

const PlaceholderIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 12px;
  background: ${({ theme }) =>
    theme.name === 'dark'
      ? 'rgba(250, 70, 22, 0.15)'
      : 'rgba(250, 70, 22, 0.08)'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fa4616;
`;

// Platform detection
const isTicketHoss = process.env.REACT_APP_PLATFORM === 'tickethoss';

// Tab configuration
const TABS = [
  { id: 'details', label: 'Details' },
  { id: 'addresses', label: 'Addresses' },
  { id: 'tickets', label: 'Tickets' },
  { id: 'staff', label: 'Staff' },
  { id: 'hardware', label: 'Hardware' },
  { id: 'divisions', label: 'Divisions' },
  { id: 'pos', label: 'POS' },
  { id: 'payments', label: 'Payments' },
  // Waivers tab only shown for non-tickethoss platforms
  ...(!isTicketHoss ? [{ id: 'waivers', label: 'Waivers' }] : []),
  { id: 'logos', label: 'Logos' },
  { id: 'notes', label: 'Notes' },
];

// Icon component
const DocumentIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10 9 9 9 8 9" />
  </svg>
);

// Inner component that uses theme
const AddAccountsContent = () => {
  const params = useParams();
  const history = useHistory();
  const location = useLocation();
  const currentAccount = params.id;

  // Get initial tab from URL query param (e.g., ?tab=staff)
  const queryParams = new URLSearchParams(location.search);
  const initialTab = queryParams.get('tab') || 'details';

  const [activeTab, setActiveTab] = useState(initialTab);

  // Sync active tab to URL so refresh preserves the current tab
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    const params = new URLSearchParams(location.search);
    params.set('tab', tabId);
    history.replace({
      pathname: location.pathname,
      search: params.toString(),
      state: location.state,
    });
  };

  const [formValues, setFormValues] = useState({
    // Business details
    business_dba: '',
    legal_name: '',
    phone: '',
    country_code: '',
    calling_code: '',
    primary_contact: '',
    email: '',
    title: '',
    // Promoter information
    promoter_name: '',
    promoter_email: '',
    promoter_phone: '',
    website: '',
    org_name: '',
    promoter_type: '',
    // Race Times
    start_time: '',
    gate_time: '',
    // Addresses
    track: {
      name: '',
      address_line_1: '',
      address_line_2: '',
      city: '',
      state: '',
      zipcode: '',
      type: 'track',
    },
    mailing: {
      name: '',
      address_line_1: '',
      address_line_2: '',
      city: '',
      state: '',
      zipcode: '',
      type: 'mailing',
    },
    shipping: {
      name: '',
      address_line_1: '',
      address_line_2: '',
      city: '',
      state: '',
      zipcode: '',
      type: 'shipping',
    },
    // Other fields
    members: [],
    readers: [],
    janam_scanners: false,
    janam_scanner_count: 0,
    kiosk_scanners: false,
    kiosk_scanner_count: 0,
    pos_printers: false,
    pos_printer_count: 0,
    scan_out: false,
    minimum_age: null,
    divisions: [],
    payout_recipients: [],
    pos: false,
    pos_settings: {
      payment: 'both',
      calculator: false,
      ticket_delivery: 'mobile',
      receipt_delivery: 'mobile',
    },
    pos_fee_dollar: '',
    pos_fee_percent: '',
    orphaned_seats: null,
    best_available: false,
    logo: null,
    logo_id: null,
    kiosk_logo: null,
    kiosk_logo_id: null,
    ticket_templates: [],
    notes: [],
    stripe_account_id: '',
    marketing_fee: false,
    international_fee: false,
    show_fee: false,
    insurance_company: '',
    waivers: [],
    agreements: [],
    inactive: false,
  });
  const [toaster, setToaster] = useState({ isOpen: false });

  const { data } = useGetAccount(parseInt(currentAccount));
  const account = data?.getAccount;
  const { updateAccountDetails, loading: saving } = useUpdateAccountDetails();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Initialize form values when account data loads
  useEffect(() => {
    if (account) {
      setFormValues((prev) => ({
        ...prev,
        // Business details
        business_dba: account.business_dba || '',
        legal_name: account.legal_name || '',
        phone: account.phone
          ? (() => {
              // Clean up any duplicate country codes like +1+1+1+17044289020
              // Extract just the digits, then take the last 10 (US number)
              const digitsOnly = account.phone.replace(/\D/g, '');
              const phoneNumber = digitsOnly.slice(-10);
              return phoneNumber;
            })()
          : '',
        country_code: account.calling_code ? `+${account.calling_code}` : '+1',
        calling_code: account.calling_code || '1',
        primary_contact: account.primary_contact || '',
        email: account.email || '',
        title: account.title || '',
        // Promoter information
        promoter_name: account.promoter_name || '',
        promoter_email: account.promoter_email || '',
        promoter_phone: account.promoter_phone || '',
        website: account.website || '',
        org_name: account.org_name || '',
        promoter_type: account.promoter_type || '',
        // Race Times
        start_time: account.start_time || '',
        gate_time: account.gate_time || '',
        // Addresses
        track: account.track ? { ...account.track, type: 'track' } : prev.track,
        mailing: account.mailing
          ? { ...account.mailing, type: 'mailing' }
          : prev.mailing,
        shipping: account.shipping
          ? { ...account.shipping, type: 'shipping' }
          : prev.shipping,
        // Other fields
        members: account.members || [],
        readers: account.readers || [],
        janam_scanners: account.janam_scanners || false,
        janam_scanner_count: account.janam_scanner_count || 0,
        kiosk_scanners: account.kiosk_scanners || false,
        kiosk_scanner_count: account.kiosk_scanner_count || 0,
        pos_printers: account.pos_printers || false,
        pos_printer_count: account.pos_printer_count || 0,
        scan_out: account.scan_out || false,
        minimum_age:
          account.minimum_age !== undefined ? account.minimum_age : null,
        divisions: (account.divisions || []).map((d) => ({
          ...d,
          image_url: d.image,
        })),
        payout_recipients: account.payout_recipients || [],
        pos: account.pos || false,
        pos_settings: account.pos_settings || {
          payment: 'both',
          calculator: false,
          ticket_delivery: 'mobile',
          receipt_delivery: 'mobile',
        },
        pos_fee_dollar: account.pos_fee_dollar ?? '',
        pos_fee_percent: account.pos_fee_percent ?? '',
        orphaned_seats: account.orphaned_seats || null,
        best_available: account.best_available || false,
        logo: account.logo || null,
        logo_id: account.logo_id || null,
        kiosk_logo: account.kiosk_logo || null,
        kiosk_logo_id: account.kiosk_logo_id || null,
        ticket_templates: account.ticket_templates || [],
        notes: account.notes || [],
        stripe_account_id: account.stripe_account_id || '',
        marketing_fee: account.marketing_fee || false,
        international_fee: account.international_fee || false,
        show_fee: account.show_fee || false,
        insurance_company: account.insurance_company || '',
        waivers: account.waivers || [],
        agreements: account.agreements || [],
        inactive: account.inactive || false,
      }));
    }
  }, [account]);

  // Handle redirects with state (toaster, new/updated templates)
  useEffect(() => {
    const {
      newTemplate,
      updatedTemplate,
      toaster: toasterState,
    } = location.state || {};

    // Handle new template from AddTicketTemplate page
    if (newTemplate) {
      setFormValues((prev) => ({
        ...prev,
        ticket_templates: [...(prev.ticket_templates || []), newTemplate],
      }));
    }

    // Handle updated template from EditTicketTemplate page
    if (updatedTemplate) {
      setFormValues((prev) => ({
        ...prev,
        ticket_templates: (prev.ticket_templates || []).map((t) =>
          t.id === updatedTemplate.id ? updatedTemplate : t
        ),
      }));
    }

    // Show toaster if present
    if (toasterState) {
      setToaster({
        isOpen: true,
        ...toasterState,
      });
    }

    // Clear the state to prevent showing again on refresh
    if (newTemplate || updatedTemplate || toasterState) {
      history.replace({
        pathname: location.pathname,
        search: location.search,
        state: {},
      });
    }
  }, [location.state, history, location.pathname, location.search]);

  // Helper to update form values (similar to Formik setFieldValue)
  const setFieldValue = (field, value) => {
    setFormValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const getTabPlaceholder = (tabId) => {
    const placeholders = {
      payments: 'Payment configuration will appear here',
    };
    return placeholders[tabId] || 'Content coming soon';
  };

  const handleCancel = () => {
    history.goBack();
  };

  const handleSave = async () => {
    // Build the input object with all saveable fields
    const input = {
      id: parseInt(currentAccount),
      // Business Details
      business_dba: formValues.business_dba,
      legal_name: formValues.legal_name,
      primary_contact: formValues.primary_contact,
      email: formValues.email,
      title: formValues.title,
      phone: formValues.phone,
      country_code: formValues.country_code?.replace('+', ''),
      calling_code: formValues.calling_code,
      // Promoter Information
      promoter_name: formValues.promoter_name,
      promoter_email: formValues.promoter_email,
      promoter_phone: formValues.promoter_phone,
      website: formValues.website,
      org_name: formValues.org_name,
      promoter_type: formValues.promoter_type,
      // Race Times
      start_time: formValues.start_time || null,
      gate_time: formValues.gate_time || null,
      // Addresses
      track: formValues.track
        ? {
            name: formValues.track.name,
            address_line_1: formValues.track.address_line_1,
            address_line_2: formValues.track.address_line_2,
            city: formValues.track.city,
            state: formValues.track.state,
            zipcode: formValues.track.zipcode,
          }
        : null,
      mailing: formValues.mailing
        ? {
            name: formValues.mailing.name,
            address_line_1: formValues.mailing.address_line_1,
            address_line_2: formValues.mailing.address_line_2,
            city: formValues.mailing.city,
            state: formValues.mailing.state,
            zipcode: formValues.mailing.zipcode,
          }
        : null,
      shipping: formValues.shipping
        ? {
            name: formValues.shipping.name,
            address_line_1: formValues.shipping.address_line_1,
            address_line_2: formValues.shipping.address_line_2,
            city: formValues.shipping.city,
            state: formValues.shipping.state,
            zipcode: formValues.shipping.zipcode,
          }
        : null,
      // Logos
      logo_id: formValues.logo_id,
      kiosk_logo_id: formValues.kiosk_logo_id,
      // POS Settings
      pos: formValues.pos,
      pos_settings: formValues.pos_settings
        ? {
            payment: formValues.pos_settings.payment,
            calculator: formValues.pos_settings.calculator,
            ticket_delivery: formValues.pos_settings.ticket_delivery,
            receipt_delivery: formValues.pos_settings.receipt_delivery,
          }
        : null,
      pos_fee_dollar: formValues.pos_fee_dollar
        ? parseFloat(formValues.pos_fee_dollar)
        : null,
      pos_fee_percent: formValues.pos_fee_percent
        ? parseFloat(formValues.pos_fee_percent)
        : null,
      // Device Settings
      janam_scanners: formValues.janam_scanners,
      janam_scanner_count: formValues.janam_scanner_count,
      kiosk_scanners: formValues.kiosk_scanners,
      kiosk_scanner_count: formValues.kiosk_scanner_count,
      pos_printers: formValues.pos_printers,
      pos_printer_count: formValues.pos_printer_count,
      scan_out: formValues.scan_out,
      minimum_age:
        formValues.minimum_age !== null && formValues.minimum_age !== undefined
          ? parseInt(formValues.minimum_age, 10)
          : null,
      // Reserved Seat Settings
      orphaned_seats: formValues.orphaned_seats || null,
      best_available: formValues.best_available || false,
      // Payment Settings
      stripe_account_id: formValues.stripe_account_id || null,
      marketing_fee: formValues.marketing_fee || false,
      international_fee: formValues.international_fee || false,
      show_fee: formValues.show_fee || false,
      // Account status
      inactive: formValues.inactive || false,
      // Waivers & Agreements - both sent as waiver_ids
      insurance_company: formValues.insurance_company || null,
      waiver_ids: [
        ...(formValues.waivers || []).map((w) =>
          typeof w === 'object' ? parseInt(w.id) : parseInt(w)
        ),
        ...(formValues.agreements || []).map((a) =>
          typeof a === 'object' ? parseInt(a.id) : parseInt(a)
        ),
      ].filter((id) => id),
    };

    const result = await updateAccountDetails(input);

    if (result.success) {
      setToaster({
        isOpen: true,
        type: 'success',
        title: 'Success!',
        message: 'Account details saved successfully.',
      });
    } else {
      setToaster({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message:
          result.error || 'Failed to save account details. Please try again.',
      });
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'details':
        return <DetailsTab values={formValues} setFieldValue={setFieldValue} />;
      case 'addresses':
        return (
          <AddressesTab values={formValues} setFieldValue={setFieldValue} />
        );
      case 'tickets':
        return (
          <TicketsTab
            ticketTemplates={formValues.ticket_templates}
            setFieldValue={setFieldValue}
            accountId={currentAccount}
          />
        );
      case 'staff':
        return (
          <StaffTab
            members={formValues.members}
            payoutRecipients={formValues.payout_recipients}
            accountId={currentAccount}
            setFieldValue={setFieldValue}
          />
        );
      case 'hardware':
        return (
          <HardwareTab
            readers={formValues.readers}
            janamScanners={formValues.janam_scanners}
            janamScannerCount={formValues.janam_scanner_count}
            kioskScanners={formValues.kiosk_scanners}
            kioskScannerCount={formValues.kiosk_scanner_count}
            posPrinters={formValues.pos_printers}
            posPrinterCount={formValues.pos_printer_count}
            scanOut={formValues.scan_out}
            minimumAge={formValues.minimum_age}
            orphanedSeats={formValues.orphaned_seats}
            bestAvailable={formValues.best_available}
            accountId={currentAccount}
            setFieldValue={setFieldValue}
          />
        );
      case 'divisions':
        return (
          <DivisionsTab
            divisions={formValues.divisions}
            setFieldValue={setFieldValue}
            accountId={currentAccount}
          />
        );
      case 'pos':
        return (
          <POSTab
            posSettings={formValues.pos_settings}
            pos={formValues.pos}
            feeSettings={{
              pos_fee_dollar: formValues.pos_fee_dollar,
              pos_fee_percent: formValues.pos_fee_percent,
            }}
            setFieldValue={setFieldValue}
          />
        );
      case 'payments':
        return (
          <PaymentsTab values={formValues} setFieldValue={setFieldValue} />
        );
      case 'waivers':
        return <WaiversTab values={formValues} setFieldValue={setFieldValue} />;
      case 'logos':
        return (
          <LogosTab
            logo={formValues.logo}
            logoId={formValues.logo_id}
            kioskLogo={formValues.kiosk_logo}
            kioskLogoId={formValues.kiosk_logo_id}
            setFieldValue={setFieldValue}
          />
        );
      case 'notes':
        return (
          <NotesTab
            notes={formValues.notes}
            setFieldValue={setFieldValue}
            accountId={currentAccount}
          />
        );
      default:
        return (
          <Card>
            <PlaceholderContent>
              <PlaceholderIcon>
                <DocumentIcon />
              </PlaceholderIcon>
              <span>{getTabPlaceholder(activeTab)}</span>
            </PlaceholderContent>
          </Card>
        );
    }
  };

  return (
    <PageWrapper>
      <Container>
        <PageHeader>
          <HeaderLeft>
            <Title>{currentAccount ? 'Edit Account' : 'Add Account'}</Title>
            <Subtitle>
              {account?.business_dba ||
                'Configure account settings and preferences'}
            </Subtitle>
          </HeaderLeft>
          <HeaderRight>
            {currentAccount && (
              <StatusToggle isInactive={formValues.inactive}>
                <StatusLabel isInactive={formValues.inactive}>
                  {formValues.inactive ? 'Inactive' : 'Active'}
                </StatusLabel>
                <StatusToggleSwitch>
                  <StatusToggleInput
                    type="checkbox"
                    checked={!formValues.inactive}
                    onChange={() =>
                      setFieldValue('inactive', !formValues.inactive)
                    }
                  />
                  <StatusToggleSlider />
                </StatusToggleSwitch>
              </StatusToggle>
            )}
            <HeaderButton>
              <ButtonV2 type="button" onClick={handleCancel}>
                Cancel
              </ButtonV2>
            </HeaderButton>
            <HeaderButton>
              <ButtonV2
                elevated
                type="button"
                onClick={handleSave}
                disabled={saving}
              >
                {saving
                  ? 'Saving...'
                  : currentAccount
                  ? 'Save Changes'
                  : 'Create Account'}
              </ButtonV2>
            </HeaderButton>
          </HeaderRight>
        </PageHeader>

        {/* Desktop tabs */}
        <DesktopTabsWrapper>
          <Tabs
            tabs={TABS}
            activeTab={activeTab}
            onTabChange={handleTabChange}
          />
        </DesktopTabsWrapper>

        {/* Mobile select dropdown */}
        <MobileTabSelect>
          <Select
            value={activeTab}
            onChange={(e) => handleTabChange(e.target.value)}
            options={TABS.map((tab) => ({
              label: tab.label,
              value: tab.id,
            }))}
          />
        </MobileTabSelect>

        {renderTabContent()}
      </Container>

      <Toaster
        isOpen={toaster.isOpen}
        onClose={() => setToaster({ isOpen: false })}
        type={toaster.type}
        title={toaster.title}
        message={toaster.message}
        duration={5000}
      />
    </PageWrapper>
  );
};

// Main component wrapped with theme provider
const AddAccountsV2 = () => {
  return (
    <EnhancedThemeProvider>
      <AddAccountsContent />
    </EnhancedThemeProvider>
  );
};

export default AddAccountsV2;
